import {
    defaultGraphQLClient,
    smsGlobalGraphQLClient,
    vocusNBNGraphQLClient,
} from '@/composables/default-urql-client'
import {
    formatFilter,
    formatPaginationData,
    formatSort,
    parsePaginationParam,
    QueryKeys,
    QueryParams,
} from '@/composables/helpers'
import { RouteQueryPrefix } from '@/composables/route'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import gql from 'graphql-tag'
import { computed, ref, watchEffect } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { toast } from 'vue-sonner'

export const useAddVocusNBNServerMutation = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (form: any) => {
            console.log('form', form)
            const response = await vocusNBNGraphQLClient.mutation(
                gql`
                    mutation (
                        $title: String!
                        $account_username: String!
                        $account_password: String!
                        $auth_client_id: String!
                        $auth_client_secret: String!
                        $api_client_id: String!
                        $api_client_secret: String!
                        $wsm_cert_pass: String!
                        $wsm_access_key: String!
                        $proxy_host: String
                        $proxy_port: Int
                        $wsm_cert: Upload!
                    ) {
                        createServer(
                            title: $title
                            account_username: $account_username
                            account_password: $account_password
                            auth_client_id: $auth_client_id
                            auth_client_secret: $auth_client_secret
                            api_client_id: $api_client_id
                            api_client_secret: $api_client_secret
                            wsm_cert_pass: $wsm_cert_pass
                            wsm_access_key: $wsm_access_key
                            wsm_cert: $wsm_cert
                            proxy_host: $proxy_host
                            proxy_port: $proxy_port
                        ) {
                            id
                        }
                    }
                `,
                {
                    ...form,
                }
            )

            if (response.error) {
                throw new Error(response.error)
            }
        },
        onSuccess: () => {
            queryClient
                .invalidateQueries({
                    queryKey: [QueryKeys.VocusNBN],
                })
                .then(() => {})

            toast.success('Vocus NBN Server created successfully.', {
                position: 'top-center',
                closeButton: false,
            })
        },
        onError: (error) => {
            toast.error('Failed to create Vocus NBN Server.', {
                position: 'top-center',
                closeButton: false,
            })
        },
    })
}

export const useServiceListQuery = (params?: QueryParams) => {
    const enabled = ref(true)
    const staleTime = ref(1000 * 60 * 5)
    const route = useRoute()
    const router = useRouter()

    const q = computed(() => {
        const keyword = route.query[`${RouteQueryPrefix.VocusNBNServices}-q`]
        return keyword ? formatFilter(['id', 'customer_name', 'service_type'], keyword) : undefined
    })
    const limit = computed(() =>
        parsePaginationParam(route.query[`${RouteQueryPrefix.VocusNBNServices}-limit`], 100)
    )
    const page = computed(() =>
        parsePaginationParam(route.query[`${RouteQueryPrefix.VocusNBNServices}-page`], 1)
    )

    watchEffect(() => {
        if (params?.staleTime?.value) {
            staleTime.value = params.staleTime.value
        }

        if (params?.enabled?.value) {
            enabled.value = params.enabled.value
        }

        if (q.value) {
            router.replace({
                query: {
                    ...route.query,
                    'vocus-nbn-services': 1,
                },
            })
        }
    })

    return useQuery({
        queryKey: [QueryKeys.VocusNBNServices, QueryKeys.Paginated],
        queryFn: async () => {
            const {
                data: { services },
            } = await vocusNBNGraphQLClient.query(
                gql`
                    query getservices(
                        $serverId: String!
                        $filter: Filter
                        $limit: Int!
                        $page: Int!
                    ) {
                        services(
                            server_id: $serverId
                            filter: $filter
                            limit: $limit
                            page: $page
                        ) {
                            data {
                                id
                                service_id
                                service_status
                                transaction_id
                                service_status
                                order_type
                                username
                                customer_name
                                contact_number
                                carrier_id
                                service_type
                                directory_id
                                avc_id
                                address
                            }
                            total
                        }
                    }
                `,
                {
                    serverId: route.params.serverId,
                    filter: q.value,
                    limit: limit.value,
                    page: page.value,
                }
            )

            return services
        },
        select: (data) => {
            const total = data.total
            const perPage = limit.value
            const currentPage = page.value
            const meta = formatPaginationData({ total, perPage, currentPage })

            return {
                ...data,
                meta,
            }
        },
        enabled: enabled.value,
        staleTime: staleTime.value,
    })
}

export const useSendSMSMutation = () => {
    const queryClient = useQueryClient()
    const route = useRoute()

    return useMutation({
        mutationFn: async (form: any) => {
            return await smsGlobalGraphQLClient.mutation(
                gql`
                    mutation sendSMS($serverId: String!, $recipient: String!, $message: String!) {
                        sendSMS(server_id: $serverId, recipient: $recipient, message: $message) {
                            id
                        }
                    }
                `,
                {
                    serverId: route.params.serverId,
                    recipient: form.recipient,
                    message: form.message,
                }
            )
        },
        onSuccess: () => {
            queryClient
                .invalidateQueries({
                    queryKey: [QueryKeys.VocusNBNServices],
                })
                .then(() => {})

            toast.success('Message sent.', {
                position: 'top-center',
                closeButton: false,
            })
        },
    })
}

export const useUpdateVocusNBNServerMutation = () => {
    const queryClient = useQueryClient()
    const route = useRoute()

    return useMutation({
        mutationFn: async (form: any) => {
            return await vocusNBNGraphQLClient.mutation(
                gql`
                    mutation (
                        $id: String!
                        $title: String!
                        $account_username: String!
                        $account_password: String!
                        $auth_client_id: String!
                        $auth_client_secret: String!
                        $api_client_id: String!
                        $api_client_secret: String!
                        $wsm_cert_pass: String!
                        $wsm_access_key: String!
                        $proxy_host: String
                        $proxy_port: Int
                    ) {
                        updateServer(
                            id: $id
                            title: $title
                            account_username: $account_username
                            account_password: $account_password
                            auth_client_id: $auth_client_id
                            auth_client_secret: $auth_client_secret
                            api_client_id: $api_client_id
                            api_client_secret: $api_client_secret
                            wsm_cert_pass: $wsm_cert_pass
                            wsm_access_key: $wsm_access_key
                            proxy_host: $proxy_host
                            proxy_port: $proxy_port
                        )
                    }
                `,
                {
                    ...form,
                    id: form.id,
                }
            )
        },
        onSuccess: () => {
            queryClient
                .invalidateQueries({
                    queryKey: [QueryKeys.VocusNBN],
                })
                .then(() => {})

            toast.success('Vocus NBN Server updated successfully.', {
                position: 'top-center',
                closeButton: false,
            })
        },
    })
}

export const useGetVocusNbnServersQuery = () => {
    const staleTime = ref(1000 * 60 * 5)

    return useQuery({
        queryKey: [QueryKeys.VocusNBN],
        queryFn: async () => {
            const {
                data: { servers: vocusNBNServers },
            } = await vocusNBNGraphQLClient.query(
                gql`
                    query getVocusNBNServers {
                        servers {
                            id
                            title
                        }
                    }
                `,
                {}
            )

            return vocusNBNServers
        },
        staleTime: staleTime.value,
    })
}

export const useGetVocusNbnServiceQuery = () => {
    const staleTime = ref(1000 * 60 * 5)
    const route = useRoute()

    console.log('route.params', route.params)
    return useQuery({
        queryKey: [QueryKeys.VocusNBNServices],
        queryFn: async () => {
            if (!route.params.serverId || !route.params.serviceId) return

            const {
                data: { service },
            } = await defaultGraphQLClient.query(
                gql`
                    query getservice($serverId: String!, $id: String!) {
                        service(server_id: $serverId, id: $id) {
                            id
                            service_id
                            service_scope
                            username
                            password
                            service_type
                            service_status
                            plan_id
                            customer_name
                            contact_number
                            copper_pair_id
                            directory_id
                            avc_id
                            cvc_id
                            address
                            order_type
                            traffic_class_one
                            traffic_class_two
                            service_level
                            nbn_instance_id
                            location_reference
                            c_tag
                            billing_provider_id
                            appointments {
                                id
                                customer_name
                                contact_number
                                records {
                                    id
                                    start_at_format_d
                                    end_at_format_d
                                    status
                                    slot
                                }
                            }
                            notifications {
                                id
                                status
                                message
                                created_at_format_d
                            }
                            tests {
                                id
                                original_id
                                status
                                created_at_format_d
                                type
                            }
                        }
                    }
                `,
                {
                    serverId: route.params.serverId,
                    id: route.params.serviceId,
                }
            )

            return service
        },
        staleTime: staleTime.value,
    })
}

export const useUpdateAppointmentContactMutation = () => {
    const queryClient = useQueryClient()
    const route = useRoute()

    return useMutation({
        mutationFn: async (form: any) => {
            return await vocusNBNGraphQLClient.mutation(
                gql`
                    mutation (
                        $id: String!
                        $serverId: String!
                        $contactName: String!
                        $contactNumber: String!
                        $message: String
                        $siteNote: String
                    ) {
                        updateServiceAppointmentContact(
                            id: $id
                            server_id: $serverId
                            contact_name: $contactName
                            contact_number: $contactNumber
                            message: $message
                            site_note: $siteNote
                        ) {
                            transaction_id
                        }
                    }
                `,
                {
                    ...form.data,
                    id: route.params.serviceId,
                    serverId: route.params.serverId,
                }
            )
        },
        onSuccess: () => {
            queryClient
                .invalidateQueries({
                    queryKey: [QueryKeys.VocusNBNServices],
                })
                .then(() => {})

            toast.success('Service appointment contact update request submitted.', {
                position: 'top-center',
                closeButton: false,
            })
        },
    })
}

export const useSendServiceMutation = () => {
    const queryClient = useQueryClient()
    const route = useRoute()

    return useMutation({
        mutationFn: async (form: any) => {
            return await vocusNBNGraphQLClient.mutation(
                gql`
                    mutation (
                        $serverId: String!
                        $serviceId: String!
                        $serviceType: String!
                        $customerName: String!
                        $contactNumber: String!
                        $copperPairId: String!
                        $directoryId: String!
                        $postcode: String!
                    ) {
                        orderNewService(
                            server_id: $serverId
                            service_id: $serviceId
                            service_type: $serviceType
                            customer_name: $customerName
                            contact_number: $contactNumber
                            copper_pair_id: $copperPairId
                            directory_id: $directoryId
                            postcode: $postcode
                        ) {
                            transaction_id
                        }
                    }
                `,
                {
                    serviceId: route.params.serviceId,
                    serverId: route.params.serverId,
                    customerName: form.data.customer_name,
                    contactNumber: form.data.contact_number,
                    serviceType: form.data.service_type,
                    copperPairId: form.data.copper_pair_id,
                    directoryId: form.data.directory_id,
                    postcode: form.data.postcode,
                }
            )
        },
        onSuccess: () => {
            queryClient
                .invalidateQueries({
                    queryKey: [QueryKeys.VocusNBNServices],
                })
                .then(() => {})

            toast.success('Order was successfully submitted.', {
                position: 'top-center',
                closeButton: false,
            })
        },
    })
}

export const useInvoiceListQuery = (params?: QueryParams) => {
    const enabled = ref(true)
    const staleTime = ref(1000 * 60 * 5)
    const route = useRoute()
    const router = useRouter()

    const q = computed(() => {
        const keyword = route.query[`${RouteQueryPrefix.VocusNBNInvoices}-q`]
        return keyword ? formatFilter(['number'], keyword) : undefined
    })
    const limit = computed(() =>
        parsePaginationParam(route.query[`${RouteQueryPrefix.VocusNBNInvoices}-limit`], 100)
    )
    const page = computed(() =>
        parsePaginationParam(route.query[`${RouteQueryPrefix.VocusNBNInvoices}-page`], 1)
    )

    watchEffect(() => {
        if (params?.staleTime?.value) {
            staleTime.value = params.staleTime.value
        }

        if (params?.enabled?.value) {
            enabled.value = params.enabled.value
        }

        if (q.value) {
            router.replace({
                query: {
                    ...route.query,
                    'vocus-nbn-invoices': 1,
                },
            })
        }
    })

    return useQuery({
        queryKey: [QueryKeys.VocusNBNInvoices, QueryKeys.Paginated],
        queryFn: async () => {
            const {
                data: { invoices },
            } = await vocusNBNGraphQLClient.query(
                gql`
                    query getInvoices($serverId: String!, $filter: Filter) {
                        invoices(server_id: $serverId, filter: $filter) {
                            id
                            filename
                            date_format_b
                            download_url
                            number
                        }
                    }
                `,
                {
                    serverId: route.params.serverId,
                    filter: q.value,
                }
            )

            return invoices
        },
        select: (data) => {
            const total = data.length
            const perPage = limit.value
            const currentPage = page.value
            const meta = formatPaginationData({ total, perPage, currentPage })

            console.log('meta', {
                ...data,
                meta,
            })
            return {
                data: data,
                meta,
            }
        },
        enabled: enabled.value,
        staleTime: staleTime.value,
    })
}
